import {
  Typography,
  Button,
  InfoItem,
  ImageGallery,
  BorderLine,
  EmptyFilesMessage,
  ImageGalleryCard,
  fullScreenButtonClick,
  Tooltip,
} from '../../design-system';
import {
  DashboardHeader,
  DashboardFormCards,
  DashboardFormCard,
  DashboardFormCustomColumn,
} from '../dashboard';
import { useStore } from '../../context';
import { showToRoles, momentTextFormat, formatPhoneNumber, formatContainerType } from '../../utils';
import { i18n } from '../../i18n';
import { useState } from 'react';
import { GateStatusToChipLabels } from '../../const';

export const GateOutsViewHeader = ({ request, onPrint }: any) => {
  const isPrintDisabled = request.status !== 'COMPLETED';

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.gateOutsAdd.title')}: {request.container.number} |{' '}
        {GateStatusToChipLabels[
          request.status as keyof typeof GateStatusToChipLabels
        ].toUpperCase()}
      </Typography>
      <Button size={'medium'} data-color={'white'} to={-1}>
        {i18n.t('button.goBack')}
      </Button>
      <Tooltip
        title={isPrintDisabled ? i18n.t('pages.gateInReceipt.completedStatusRequired') : ''}
        placement={'top'}
        arrow
      >
        <span>
          <Button size={'medium'} onClick={onPrint} disabled={isPrintDisabled}>
            {i18n.t('button.print')}
          </Button>
        </span>
      </Tooltip>
    </DashboardHeader>
  );
};

export const GateOutsViewForm = ({ request }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const [zoomImageIndex, setZoomImageIndex] = useState(0);

  const handleZoomPhotoClick = (i: number) => {
    setZoomImageIndex(i);
    fullScreenButtonClick();
  };

  return (
    <>
      <DashboardFormCards id={'gate-out-view'}>
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('common.generalInformation')}
          </Typography>
          {request.container.number && (
            <InfoItem label={i18n.t('label.containerNumber')} value={request.container.number} />
          )}
          <InfoItem
            label={i18n.t('label.containerType')}
            value={formatContainerType(request.container.type)}
          />
          <InfoItem
            className="no-print"
            label={i18n.t('label.createdAt')}
            value={request.createdAt && momentTextFormat(request.createdAt, user)}
          />
          <InfoItem
            className="no-print"
            label={i18n.t('label.from')}
            value={request.validFrom && momentTextFormat(request.validFrom, user)}
          />
          <InfoItem
            className="no-print"
            label={i18n.t('label.to')}
            value={request.validTo && momentTextFormat(request.validTo, user)}
          />
          <InfoItem
            label={i18n.t('label.completedAt')}
            value={request.completedAt && momentTextFormat(request.completedAt, user)}
          />
          {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER'], user) && (
            <>
              <InfoItem
                className="no-print"
                label={i18n.t('label.createdBy')}
                value={request.creatorName}
              />
              <InfoItem label={i18n.t('label.completedBy')} value={request.authorizerName} />
            </>
          )}
          {(request.driver?.licensePlateNumber === 'TRANSFERRED' ||
            request.driver?.licensePlateNumber === 'MARKET') && (
            <InfoItem
              className="no-print"
              label={i18n.t('label.gateOutType')}
              value={request.driver?.licensePlateNumber}
            />
          )}
          {request.transferredTo && (
            <InfoItem
              className="no-print"
              label={i18n.t('label.gateOutCode')}
              value={request.transferredTo}
            />
          )}
        </DashboardFormCard>
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.gateInsView.transportInformation')}
          </Typography>
          <InfoItem label={i18n.t('label.transport')} value={'ground'} />
          <InfoItem
            label={i18n.t('label.licensePlateNumber')}
            value={
              request.driver?.licensePlateNumber === 'TRANSFERRED' ||
              request.driver?.licensePlateNumber === 'MARKET'
                ? ''
                : request.driver?.licensePlateNumber ||
                  request.authorizedLicensePlateNumbers.join(', ')
            }
            breakSpaces
          />
          <InfoItem
            label={i18n.t('label.driverName')}
            value={[request.driver?.firstName, request.driver?.lastName]}
          />
          <InfoItem
            label={i18n.t('label.phoneNumber')}
            value={formatPhoneNumber(request.driver?.phoneNumber)}
          />
          <InfoItem label={i18n.t('label.bookingNumber')} value={request.releaseNumber} />
          <InfoItem label={i18n.t('label.comment')} value={request.comment} />
        </DashboardFormCard>
        <DashboardFormCard
          className="no-print"
          paperSize={'full-size'}
          rows={'max-content max-content 1fr'}
        >
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('common.images')}
          </Typography>
          <BorderLine space={'dashboard-form-card'} />
          <DashboardFormCustomColumn
            columns={'1fr 1fr 1fr'}
            alignContent={!request.images.length ? 'center' : 'initial'}
          >
            {!request.images.length && (
              <EmptyFilesMessage variant={'textM'} align={'center'} gridColumn={'1 / 4'}>
                {i18n.t('pages.gateOutsView.noImages')}
              </EmptyFilesMessage>
            )}
            {request.images.map((img: any, i: any) => {
              return (
                <ImageGalleryCard
                  key={i}
                  img={img.url}
                  handleZoomClick={() => handleZoomPhotoClick(i)}
                  handleDeleteImageClick={() => null}
                  hideDeleteButton={true}
                />
              );
            })}
          </DashboardFormCustomColumn>
        </DashboardFormCard>
      </DashboardFormCards>
      <ImageGallery
        photos={request.images?.map(({ url }: any) => url)}
        startIndex={zoomImageIndex}
      />
    </>
  );
};
